import GoogleButton from './google-button';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../state/zustand-store';
import {
  checkAuth,
  checkAuthQuery,
  clearStaleFirebaseUser,
  getUserSettings,
  logClientError,
  userSettingsQuery,
} from '../../api/users-api';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { FirebaseError } from 'firebase/app';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../../shared/firebase/firebase-config';
import {
  describeAuthMismatch,
  describeFirebaseUser,
  describeGoogleIdToken,
  sanitizedFirebaseCustomData,
} from './login-auth-diagnostics';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { AuthResponse } from '../../../types';
import styles from './login-page.module.css';
import GpanelLogoSvg from './gpanel-logo-svg';
import BackgroundImage from '../../assets/gpanel-arrow-background.svg';
import { useQueryClient } from '@tanstack/react-query';
import { pagesConfig } from '../../shared/pages-config';
import { tiers } from '../../shared/gPanel-tiers';

import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ContactSupport from '@mui/icons-material/ContactSupport';

const DEFAULT_LANDING_ROUTE = '/';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const { setSnackbar } = useStore();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    queryClient.clear();
  }, []);

  async function resolveLandingPath() {
    const explicitFrom = params.get('from');
    if (explicitFrom) {
      return explicitFrom;
    }

    try {
      const settings = await getUserSettings();
      queryClient.setQueryData(userSettingsQuery().queryKey, settings);

      const usersTier = tiers.find(
        (tier) => tier.value === settings.currentTier
      );
      const hasDashboardTier =
        (usersTier ?? tiers[0]).rank >=
        pagesConfig.commandDashboard.minimumTier;
      return isFeatureEnabled('commandDashboardUi', settings.features) &&
        hasDashboardTier
        ? `/${pagesConfig.commandDashboard.link}`
        : DEFAULT_LANDING_ROUTE;
    } catch {
      return DEFAULT_LANDING_ROUTE;
    }
  }

  async function handleLoginWithFirebaseAuth(
    googleIdToken: string,
    isRetry = false
  ) {
    setIsLoggingIn(true);
    const beforeUser = auth.currentUser;
    const authDiagnostics = {
      firebaseProjectId: auth.app.options.projectId ?? '',
      firebaseAuthDomain: auth.app.options.authDomain ?? '',
      ...describeFirebaseUser(beforeUser, 'before'),
      ...describeGoogleIdToken(googleIdToken),
      ...describeAuthMismatch(beforeUser, googleIdToken),
    };
    try {
      await signInWithCredential(
        auth,
        GoogleAuthProvider.credential(googleIdToken)
      );

      const firebaseIdToken = await auth.currentUser?.getIdToken();

      try {
        if (firebaseIdToken) {
          const response = await checkAuth(firebaseIdToken, googleIdToken);
          queryClient.setQueryData(checkAuthQuery().queryKey, response);

          if (response.hasUpdatedFirebaseEmail) {
            // update the firebase id token to get the latest email
            await auth.currentUser?.getIdToken(true);
          }
        }

        navigate(await resolveLandingPath(), { replace: true });
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorResponse = error.response?.data as AuthResponse;
          if (errorResponse.result === 'IP_RESTRICTED') {
            navigate('/ip-restricted');
          } else if (errorResponse.result === 'INVALID_ADMIN') {
            navigate('/invalid-admin', {
              state: {
                hasNoAdmin: errorResponse.hasNoAdmin,
                oldAdmin: errorResponse.oldAdmin,
                currentUserIsAdmin: errorResponse.currentUserIsAdmin,
                loggedInUserEmail: errorResponse.loggedInUserEmail,
              },
            });
          } else if (errorResponse.result === 'INVALID_DOMAIN') {
            navigate('/invalid-domain', {
              state: {
                loggedInUserEmail: errorResponse.loggedInUserEmail,
                primaryDomain: errorResponse.primaryDomain,
              },
            });
          } else if (errorResponse.result === 'ERROR') {
            navigate('/error');
          } else if (errorResponse.result === 'MESSAGE') {
            navigate('/error-message', {
              state: {
                messageSubject: errorResponse.messageSubject,
                messageBody: errorResponse.messageBody,
              },
            });
          } else if (errorResponse.result === 'TRIAL') {
            navigate('/trial');
          } else if (errorResponse.result === 'INVALID_SECONDARY_DOMAIN') {
            navigate('/invalid-secondary-domain');
          } else {
            logClientError(
              error as Error,
              'Auth Flow',
              window.location.href,
              'error'
            );

            setSnackbar({
              open: true,
              message: `There was an error logging you in. Please reach out to support@promevo.com.`,
              severity: 'error',
            });
          }
        }
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/provider-already-linked' && !isRetry) {
          // Clear the stale login record, then retry once without showing an error.
          await clearStaleFirebaseUser(googleIdToken).catch(() => undefined);
          return await handleLoginWithFirebaseAuth(googleIdToken, true);
        }

        const details: Record<string, string> = {
          firebaseCode: error.code,
          firebaseName: error.name,
          ...authDiagnostics,
          ...describeFirebaseUser(auth.currentUser, 'after'),
          ...sanitizedFirebaseCustomData(error.customData),
        };

        // Permanent auth failures — not user-retry/transient issues
        const nonTransientFirebaseCodes = new Set([
          'auth/provider-already-linked',
          'auth/credential-already-in-use',
          'auth/account-exists-with-different-credential',
          'auth/user-disabled',
          'auth/operation-not-allowed',
        ]);

        logClientError(
          error,
          'Auth Flow',
          window.location.href,
          nonTransientFirebaseCodes.has(error.code) ? 'error' : 'warning',
          details
        );

        setSnackbar({
          open: true,
          message: `Login failed. Please try again.`,
          severity: 'error',
        });
      }
    } finally {
      setIsLoggingIn(false);
    }
  }

  const wasTimedOut = searchParams.has('session-timeout');

  return (
    <>
      <title>gPanel | Login</title>
      <Stack width={'100vw'} height={'100vh'} flexDirection="row">
        <img
          src={BackgroundImage}
          alt="gPanel Logo"
          width={'100%'}
          height={'100%'}
          style={{
            position: 'absolute',
            objectFit: 'cover',
            marginLeft: '-100px',
          }}
        />
        <GpanelLogoSvg />
        <Stack
          width="60vw"
          justifyContent="center"
          alignItems="center"
          sx={{ zIndex: 3 }}
        >
          <Card
            sx={{
              height: 420,
              width: 500,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 4,
              }}
            >
              <Typography className={styles.cardText} sx={{ mt: 2 }}>
                gPanel® by Promevo is an exclusive Google Workspace™
                management and reporting solution. This centralized user
                management and security interface delivers peace of mind with
                its robust suite of features.
              </Typography>
              <Typography className={styles.cardText}>
                The gPanel® interface provides administrators with all the
                tools they need to manage users effectively in their domain
                while safeguarding sensitive data. Not only does gPanel®
                software give you the ability to take precise administrative
                action, it also allows you to oversee files, email
                communications, and other internal processes within your
                company. Regardless of the size of your business, any Google
                Workspace™ domain can benefit from using gPanel® software.
              </Typography>
              <Link
                className={styles.cardLink}
                href="https://gpanel.io/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Learn More
              </Link>
            </Box>
          </Card>
          <Stack
            flexDirection="row"
            gap={1}
            sx={{
              mt: 1,
              width: 500,
              justifyContent: 'flex-end',
            }}
          >
            <Link
              href="https://gpanel.io/contact-us"
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconButton
                sx={{
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#a9dccc' },
                }}
                size="small"
              >
                <ContactSupport
                  sx={{
                    color: '#233166',
                  }}
                />
              </IconButton>
            </Link>
            <Link
              href="https://www.linkedin.com/company/promevo/"
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconButton
                sx={{
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#a9dccc' },
                }}
                size="small"
              >
                <SvgIcon>
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.16181 3.53238C0.784053 3.18164 0.596191 2.7475 0.596191 2.23095C0.596191 1.7144 0.785058 1.26115 1.16181 0.909415C1.53957 0.558681 2.02583 0.382812 2.6216 0.382812C3.21737 0.382812 3.68455 0.558681 4.0613 0.909415C4.43906 1.26015 4.62692 1.70133 4.62692 2.23095C4.62692 2.76057 4.43806 3.18164 4.0613 3.53238C3.68355 3.88311 3.20431 4.05898 2.6216 4.05898C2.03889 4.05898 1.53957 3.88311 1.16181 3.53238ZM4.30946 5.54433V16.2975H0.912653V5.54433H4.30946Z"
                      fill="#233166"
                    />
                    <path
                      d="M15.6171 6.60635C16.3576 7.41033 16.7273 8.51379 16.7273 9.91873V16.1073H13.5013V10.3549C13.5013 9.64638 13.3174 9.09566 12.9507 8.70372C12.584 8.31178 12.0897 8.11481 11.4708 8.11481C10.852 8.11481 10.3576 8.31078 9.99094 8.70372C9.62424 9.09566 9.44038 9.64638 9.44038 10.3549V16.1073H6.19531V5.51395H9.44038V6.9189C9.76891 6.45058 10.212 6.08076 10.7686 5.80841C11.3252 5.53607 11.9511 5.40039 12.6473 5.40039C13.8871 5.40039 14.8777 5.80238 15.6171 6.60535V6.60635Z"
                      fill="#233166"
                    />
                  </svg>
                </SvgIcon>
              </IconButton>
            </Link>
            <Link
              href="https://www.youtube.com/user/Promevo"
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconButton
                sx={{
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#a9dccc' },
                }}
                size="small"
              >
                <SvgIcon>
                  <svg
                    width="19"
                    height="14"
                    viewBox="0 0 19 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.3 4.33708C18.1875 3.25071 17.9454 2.04977 17.0542 1.41865C16.364 0.929232 15.4568 0.911138 14.6099 0.912143C12.8195 0.912143 11.0282 0.915162 9.23787 0.916167C7.51587 0.918177 5.79386 0.919181 4.07185 0.921191C3.3525 0.921191 2.65325 0.865916 1.98514 1.17746C1.41147 1.44478 0.962386 1.9533 0.692129 2.51909C0.317386 3.30598 0.239026 4.19739 0.193816 5.06769C0.110428 6.65252 0.11947 8.24137 0.218932 9.8252C0.292273 10.9809 0.478135 12.2582 1.37129 12.9949C2.16297 13.6471 3.28016 13.6793 4.30694 13.6803C7.56609 13.6833 10.8263 13.6863 14.0864 13.6883C14.5044 13.6893 14.9404 13.6813 15.3664 13.635C16.2043 13.5446 17.003 13.3044 17.5415 12.6833C18.085 12.0572 18.2247 11.1859 18.3071 10.3609C18.508 8.35896 18.506 6.33797 18.3 4.33708ZM7.35311 10.1066V4.49385L12.2117 7.29972L7.35311 10.1066Z"
                      fill="#233166"
                    />
                  </svg>
                </SvgIcon>
              </IconButton>
            </Link>
          </Stack>
        </Stack>
        <Stack
          sx={{ bgcolor: '#F1F1F1', width: '40vw' }}
          justifyContent="center"
          alignItems="center"
          zIndex={3}
        >
          <Card
            sx={{
              minWidth: 280,
              width: 'fit-content',
              maxWidth: '100%',
              mx: 5,
            }}
          >
            <Stack
              gap={2}
              sx={{ p: 2 }}
              justifyContent="center"
              alignItems="center"
              width={'100%'}
            >
              <Stack gap={1} alignItems="center">
                <Stack alignItems="center">
                  <SvgIcon sx={{ width: 60, height: 'auto' }}>
                    <svg
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 145.55 110.41"
                    >
                      <g>
                        <path
                          fill="#233166"
                          d="m0,0v27.85s5.23,0,5.23,0V5.23s99.95,0,99.95,0v99.95s-37.18,0-37.18,0v5.23s42.4,0,42.4,0V0S0,0,0,0Z"
                        />
                        <path
                          fill="#233166"
                          d="m133.9,23.08c-2.22,0-4.21-.49-5.96-1.47s-3.14-2.34-4.16-4.08c-1.02-1.74-1.53-3.73-1.53-5.99s.51-4.25,1.53-5.99c1.02-1.74,2.4-3.1,4.16-4.08s3.75-1.47,5.96-1.47,4.21.49,5.96,1.47c1.76.98,3.15,2.34,4.16,4.08,1.02,1.74,1.53,3.74,1.53,5.99s-.51,4.25-1.53,5.99c-1.02,1.74-2.41,3.1-4.16,4.08-1.76.98-3.74,1.47-5.96,1.47Zm0-2.28c2.81,0,5.06-.84,6.74-2.52,1.68-1.68,2.53-3.93,2.53-6.74s-.84-5.06-2.53-6.74c-1.68-1.68-3.93-2.52-6.74-2.52s-5.07.84-6.77,2.52c-1.7,1.68-2.55,3.93-2.55,6.74s.85,5.06,2.55,6.74c1.7,1.68,3.96,2.52,6.77,2.52Zm-3.99-3.21V5.27h4.71c1.37,0,2.41.33,3.11,1,.7.66,1.06,1.44,1.06,2.33,0,.74-.18,1.38-.53,1.91s-.92.95-1.69,1.25v.45c.63.04,1.08.25,1.36.64.28.39.42.86.42,1.41v3.33h-2.27v-3.39c0-.78-.39-1.16-1.16-1.16h-2.78v4.55h-2.22Zm2.22-6.55h2.55c.63,0,1.09-.19,1.39-.58.29-.39.44-.83.44-1.3,0-.52-.15-.95-.44-1.3-.3-.35-.76-.53-1.39-.53h-2.55v3.72Z"
                        />
                      </g>
                      <polygon
                        fill="#233166"
                        points="55.21 15.88 94.52 55.19 94.52 15.88 55.21 15.88"
                      />
                      <path
                        fill="#233166"
                        d="m42.93,45.79c0,.57-.46,1.03-1.03,1.03-.36,0-.69-.19-.88-.49-1.21-1.89-3.01-3.67-5.38-5.34-2.58-1.81-6.29-2.72-11.12-2.72-4.16,0-7.98,1.01-11.47,3.02-3.49,2.01-6.29,4.86-8.4,8.55-2.11,3.69-3.17,8.15-3.17,13.38v1.51c0,5.17,1.04,9.61,3.12,13.33,2.08,3.72,4.88,6.57,8.4,8.55,3.52,1.98,7.36,2.97,11.52,2.97,3.29,0,6.02-.4,8.2-1.21,2.18-.81,3.94-1.84,5.28-3.12,1.16-1.11,2.1-2.19,2.81-3.25.2-.29.52-.48.87-.48h0c.58,0,1.04.47,1.04,1.04v14.75c0,2.01-.97,3.02-2.92,3.02H9.63s0,9.06,0,9.06h33.4c3.08,0,5.52-.91,7.29-2.72,1.78-1.81,2.67-4.23,2.67-7.24v-59.76s-10.06,0-10.06,0v6.11Zm-.1,18.64c0,5.03-1.46,8.97-4.38,11.82-2.92,2.85-6.62,4.28-11.12,4.28-4.43,0-8.1-1.42-11.02-4.28-2.92-2.85-4.38-6.79-4.38-11.82v-.91c0-5.16,1.46-9.14,4.38-11.92,2.92-2.78,6.59-4.18,11.02-4.18,4.56,0,8.28,1.39,11.17,4.18,2.88,2.78,4.33,6.76,4.33,11.92v.91Z"
                      />
                    </svg>
                  </SvgIcon>
                  <Typography
                    sx={{
                      color: '#233166',
                      fontSize: 32,
                      fontWeight: 'light',
                    }}
                  >
                    LOGIN
                  </Typography>
                </Stack>
                <Box sx={{ my: 2 }}>
                  {isLoggingIn ? (
                    <GoogleButton
                      onClick={() => {}}
                      disabled={isLoggingIn}
                      isLoading={isLoggingIn}
                    />
                  ) : (
                    <GoogleLogin
                      onSuccess={(credentialResponse) => {
                        handleLoginWithFirebaseAuth(
                          credentialResponse.credential || ''
                        );
                      }}
                      onError={() => {
                        setSnackbar({
                          open: true,
                          severity: 'error',
                          message: 'Login failed',
                        });
                      }}
                      width={250}
                      use_fedcm_for_prompt
                    />
                  )}
                </Box>
              </Stack>
              <Stack alignItems="center" maxWidth={300}>
                {wasTimedOut ? (
                  <Alert severity="info" sx={{ alignItems: 'center' }}>
                    You've been logged out due to inactivity. Please log back in
                    to continue.
                  </Alert>
                ) : null}
              </Stack>
              <Stack
                sx={{
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontSize: '12px' }}>
                  Don't have an account?
                </Typography>
                <Link
                  className={styles.cardLink}
                  href="https://gpanel.io/demo"
                  rel="noopener noreferrer"
                  target="_blank"
                  sx={{ fontSize: '13px', ml: 'unset !important' }}
                >
                  Schedule a demo
                </Link>
              </Stack>
            </Stack>
          </Card>
          <Stack
            flexDirection="row"
            gap={2}
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <Link
              href="https://promevo.com/privacy-policy"
              rel="noopener noreferrer"
              target="_blank"
              color="#233166"
            >
              <Typography sx={{ p: 0, fontSize: 12 }}>
                Privacy Policy
              </Typography>
            </Link>
            <Divider orientation="vertical" flexItem />
            <Link
              href="https://gpanel.io/knowledge/getting-started/attributions"
              rel="noopener noreferrer"
              target="_blank"
              color="#233166"
            >
              <Typography sx={{ p: 0, fontSize: 12 }}>Attributions</Typography>
            </Link>
            <Divider orientation="vertical" flexItem />
            <Link
              href="https://gpanel.io/terms-of-service"
              rel="noopener noreferrer"
              target="_blank"
              color="#233166"
            >
              <Typography sx={{ p: 0, fontSize: 12 }}>
                Terms of Service
              </Typography>
            </Link>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
